import { useState, useRef, useEffect } from "react";
import { MapPin, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * AddressAutocomplete - Simple address input with optional Google Places suggestions
 * Falls back gracefully if Google API is not available
 */
export default function AddressAutocomplete({
  value = "",
  onChange,
  placeholder = "Inserisci indirizzo...",
  className = "",
  disabled = false
}) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const debounceTimer = useRef(null);
  const wrapperRef = useRef(null);

  // Sync with external value
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Initialize Google services (non-blocking)
  useEffect(() => {
    const init = () => {
      try {
        if (window.google?.maps?.places) {
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
          const div = document.createElement('div');
          placesService.current = new window.google.maps.places.PlacesService(div);
        }
      } catch (e) {
        console.warn('Google Places not available');
      }
    };
    init();
    // Retry after 2 seconds if not loaded
    const timer = setTimeout(init, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchSuggestions = (input) => {
    if (!input || input.length < 3 || !autocompleteService.current) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    try {
      autocompleteService.current.getPlacePredictions(
        { input, componentRestrictions: { country: 'it' }, types: ['address'] },
        (predictions, status) => {
          setLoading(false);
          if (status === 'OK' && predictions) {
            setSuggestions(predictions.slice(0, 5));
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    } catch (e) {
      setLoading(false);
      setSuggestions([]);
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    
    // Debounce API calls
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(val);
    }, 400);
  };

  const handleSelect = (suggestion) => {
    const address = suggestion.description;
    setInputValue(address);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Get details in background (non-blocking)
    if (placesService.current) {
      try {
        placesService.current.getDetails(
          { placeId: suggestion.place_id, fields: ['formatted_address', 'geometry', 'address_components'] },
          (place, status) => {
            if (status === 'OK' && place) {
              const components = {};
              place.address_components?.forEach(comp => {
                if (comp.types.includes('locality')) components.city = comp.long_name;
                if (comp.types.includes('administrative_area_level_2')) components.province = comp.short_name;
              });
              onChange?.(place.formatted_address || address, components);
            } else {
              onChange?.(address);
            }
          }
        );
      } catch (e) {
        onChange?.(address);
      }
    } else {
      onChange?.(address);
    }
  };

  const handleBlur = () => {
    // Delay to allow click on suggestions
    setTimeout(() => {
      setShowSuggestions(false);
      // Save current value on blur
      if (inputValue && inputValue !== value) {
        onChange?.(inputValue);
      }
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setShowSuggestions(false);
      onChange?.(inputValue);
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const clearInput = () => {
    setInputValue("");
    setSuggestions([]);
    setShowSuggestions(false);
    onChange?.("");
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
        <Input
          type="text"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-10 h-12 rounded-xl bg-[#F5F5F7] dark:bg-white/10 border-0"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-[#86868B] animate-spin" size={18} />
        )}
        {!loading && inputValue && (
          <button
            type="button"
            onClick={clearInput}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#86868B] hover:text-[#1D1D1F]"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#2C2C2E] rounded-xl shadow-lg border border-black/5 dark:border-white/10 overflow-hidden">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_id}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-[#F5F5F7] dark:hover:bg-white/10 flex items-start gap-3 transition-colors"
            >
              <MapPin size={16} className="text-[#FF6B4A] mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#1D1D1F] dark:text-white truncate">
                  {suggestion.structured_formatting?.main_text || suggestion.description}
                </p>
                <p className="text-xs text-[#86868B] truncate">
                  {suggestion.structured_formatting?.secondary_text || ''}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
